import * as React from "react";
import {
  addDays,
  endOfMonth,
  format,
  isValid,
  startOfMonth,
  subYears,
  startOfYear,
  endOfYear,
  isAfter,
  isBefore,
  differenceInDays,
  startOfWeek,
  endOfWeek,
  addWeeks,
  subMonths,
  startOfQuarter,
  endOfQuarter,
  subQuarters,
  startOfDay,
  endOfDay,
} from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import TimePicker from "../TimePicker";
import { FaArrowRightLong } from "react-icons/fa6";

function DatePicker2({
  value,
  onChange,
  mode = "single",
  placeholder = "Select date",
  disabled = false,
  showYearDropdown = true,
  showTimePicker = false,
  allowFuture = true,
  allowPast = true,
  minDate,
  maxDate,
  excludedDates = [],
  maxSelectableRange,
}) {
  const [open, setOpen] = React.useState(false);
  const [currentMonth, setCurrentMonth] = React.useState(
    value?.from ? new Date(value.from) : value ? new Date(value) : new Date()
  );

  const isRangeMode = mode === "range";

  React.useEffect(() => {
    if (value) {
      const targetDate = isRangeMode ? value.from : value;
      if (targetDate && isValid(new Date(targetDate))) {
        setCurrentMonth(new Date(targetDate));
      }
    }
  }, [value, isRangeMode]);

  const presets = {
    today: {
      label: "Today",
      range: {
        from: startOfDay(new Date()),
        to: endOfDay(new Date()),
      },
    },
    yesterday: {
      label: "Yesterday",
      range: {
        from: startOfDay(addDays(new Date(), -1)),
        to: endOfDay(addDays(new Date(), -1)),
      },
    },
    last7Days: {
      label: "Last 7 Days",
      range: {
        from: startOfDay(addDays(new Date(), -6)),
        to: endOfDay(new Date()),
      },
    },
    last30Days: {
      label: "Last 30 Days",
      range: {
        from: startOfDay(addDays(new Date(), -29)),
        to: endOfDay(new Date()),
      },
    },
    thisWeek: {
      label: "This Week",
      range: {
        from: startOfWeek(new Date(), { weekStartsOn: 1 }),
        to: endOfWeek(new Date(), { weekStartsOn: 1 }),
      },
    },
    lastWeek: {
      label: "Last Week",
      range: {
        from: startOfWeek(subMonths(new Date(), 1), { weekStartsOn: 1 }),
        to: endOfWeek(subMonths(new Date(), 1), { weekStartsOn: 1 }),
      },
    },
    thisMonth: {
      label: "This Month",
      range: {
        from: startOfMonth(new Date()),
        to: endOfMonth(new Date()),
      },
    },
    lastMonth: {
      label: "Last Month",
      range: {
        from: startOfMonth(subMonths(new Date(), 1)),
        to: endOfMonth(subMonths(new Date(), 1)),
      },
    },
    thisQuarter: {
      label: "This Quarter",
      range: {
        from: startOfQuarter(new Date()),
        to: endOfQuarter(new Date()),
      },
    },
    lastQuarter: {
      label: "Last Quarter",
      range: {
        from: startOfQuarter(subQuarters(new Date(), 1)),
        to: endOfQuarter(subQuarters(new Date(), 1)),
      },
    },
    thisYear: {
      label: "This Year",
      range: {
        from: startOfYear(new Date()),
        to: endOfYear(new Date()),
      },
    },
    lastYear: {
      label: "Last Year",
      range: {
        from: startOfYear(subYears(new Date(), 1)),
        to: endOfYear(subYears(new Date(), 1)),
      },
    },
    lifetime: {
      label: "Lifetime",
      range: {
        from: new Date(2000, 0, 1),
        to: endOfDay(new Date()),
      },
    },
  };

  const getDisabledDays = () => {
    const restrictions = [];

    if (!allowPast && !minDate) {
      restrictions.push({ before: new Date() });
    }

    if (!allowFuture && !maxDate) {
      restrictions.push({ after: new Date() });
    }

    if (minDate) {
      restrictions.push({ before: new Date(minDate) });
    }

    if (maxDate) {
      restrictions.push({ after: new Date(maxDate) });
    }

    if (excludedDates.length > 0) {
      restrictions.push(...excludedDates.map((date) => new Date(date)));
    }

    return restrictions;
  };

  const handlePresetSelect = (presetKey) => {
    const selectedRange = presets[presetKey]?.range;
    if (selectedRange) {
      onChange?.(selectedRange);
      setCurrentMonth(new Date(selectedRange.from));
      setOpen(false);
    }
  };

  const presetFooter = isRangeMode ? (
    <div className="w-full flex flex-wrap gap-1 border-t border-background/10 pt-2">
      {Object.keys(presets).map((key) => (
        <button
          key={key}
          onClick={() => handlePresetSelect(key)}
          className="px-2 py-1 text-xs rounded-md bg-background/10 text-background/80 hover:bg-primary/20 hover:text-primary transition-colors"
        >
          {presets[key].label}
        </button>
      ))}
    </div>
  ) : null;

  const handleDateChange = (selected) => {
    if (mode === "single") {
      if (!selected) return;

      if (
        (!allowFuture && isAfter(selected, new Date())) ||
        (!allowPast && isBefore(selected, new Date()))
      ) {
        return;
      }

      let finalDate = selected;
      if (showTimePicker && value && isValid(value)) {
        finalDate = new Date(
          selected.getFullYear(),
          selected.getMonth(),
          selected.getDate(),
          value.getHours(),
          value.getMinutes()
        );
      }

      onChange?.(finalDate);
      setCurrentMonth(new Date(finalDate));
      setOpen(false);
    } else {
      if (selected?.from && selected?.to && differenceInDays(selected.to, selected.from) > 0) {
        if (maxSelectableRange) {
          const daysDifference = differenceInDays(selected.to, selected.from);
          if (daysDifference > maxSelectableRange) {
            selected.to = addDays(selected.from, maxSelectableRange);
          }
        }

        if (
          (!allowFuture && isAfter(selected.to, new Date())) ||
          (!allowPast && isBefore(selected.from, new Date()))
        ) {
          return;
        }
      }

      onChange?.(selected);

      if (selected?.from && selected?.to && differenceInDays(selected.to, selected.from) > 0) {
        setOpen(false);
      } else if (selected?.from) {
        setCurrentMonth(new Date(selected.from));
      }
    }
  };

  const handleYearChange = (year) => {
    const newYear = parseInt(year, 10);
    const newMonth = new Date(currentMonth);
    newMonth.setFullYear(newYear);
    setCurrentMonth(newMonth);
  };

  const handleTimeChange = (time) => {
    if (mode !== "single" || !value || !isValid(value)) return;

    const [hours, minutes] = time.split(":").map(Number);
    const updatedDate = new Date(value);
    updatedDate.setHours(hours);
    updatedDate.setMinutes(minutes);
    onChange?.(updatedDate);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          className={`h-10 w-full flex rounded-xl cursor-pointer justify-between border-secondary/40 items-center gap-2 text-body2 font-ptsans text-secondary/90 outline-none hover:!border-secondary/60 focus:!border-secondary/65 focus:!outline-2 focus:outline-secondary/30 ${
            disabled
              ? "!bg-background_2/80 border-none !cursor-not-allowed"
              : "border-secondary/35 bg-transparent"
          }`}
          onClick={(e) => disabled && e.preventDefault()}
        >
          {isRangeMode && value?.from && value?.to ? (
            <>
              <div className="bg-secondary/95 min-w-20 text-body2 font-ptsans text-background/90 border border-secondary p-2 rounded-xl outline-none cursor-pointer flex-1 md:flex-none">
                {format(value.from, "dd-MM-yyyy")}
              </div>
              <FaArrowRightLong className="text-secondary/60 text-body2 font-ptsans" />
              <div className="bg-secondary/95 text-body2 min-w-20 font-ptsans text-background/90 border border-secondary p-2 rounded-xl outline-none cursor-pointer flex-1 md:flex-none">
                {format(value.to, "dd-MM-yyyy")}
              </div>
            </>
          ) : !isRangeMode && value && isValid(new Date(value)) ? (
            <span>
              {format(
                value,
                showTimePicker ? "dd-MM-yyyy HH:mm" : "dd-MM-yyyy"
              )}
            </span>
          ) : (
            <div className="text-secondary/60 text-sm md:text-body1 flex items-center gap-2">
              <div className="bg-secondary/95 min-w-20 text-body2 font-ptsans text-background_2/90 border border-secondary p-2 rounded-xl outline-none cursor-pointer flex-1 md:flex-none">
                {"From Date"}
              </div>
              <FaArrowRightLong className="text-secondary/60 text-body2 font-ptsans" />
              <div className="bg-secondary/95 text-body2 min-w-20 font-ptsans text-background_2/90 border border-secondary p-2 rounded-xl outline-none cursor-pointer flex-1 md:flex-none">
                {"To Date"}
              </div>
            </div>
          )}
        </div>
      </PopoverTrigger>

      <PopoverContent className="w-auto bg-secondary border-2 border-background/20 rounded-2xl shadow-md">
        <div className="w-min flex flex-col gap-2 p-2">
          {(showYearDropdown || (!isRangeMode && showTimePicker)) && (
            <div className="flex gap-2 items-center justify-between">
              {showYearDropdown && (
                <Select
                  onValueChange={handleYearChange}
                  value={currentMonth.getFullYear().toString()}
                >
                  <SelectTrigger className="w-fit h-8 border-background/30 text-background/90 text-xs rounded-lg gap-1">
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent className="text-xs rounded-md max-h-56">
                    {Array.from(
                      { length: 50 },
                      (_, i) => new Date().getFullYear() - i
                    ).map((year) => (
                      <SelectItem key={year} value={String(year)} className="font-semibold">
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {!isRangeMode && showTimePicker && (
                <div className="[&_.select-trigger]:h-8 [&_.select-trigger]:rounded-md">
                  <TimePicker
                    value={value && isValid(value) ? format(value, "HH:mm") : "00:00"}
                    onChange={handleTimeChange}
                  />
                </div>
              )}
            </div>
          )}

          <Calendar
            mode={mode}
            selected={value}
            onSelect={handleDateChange}
            month={currentMonth}
            onMonthChange={setCurrentMonth}
            numberOfMonths={isRangeMode ? 2 : 1}
            disabled={disabled ? true : getDisabledDays()}
            navLayout="around"
          />

          {presetFooter}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default DatePicker2;
